import type {ReactFrontendTool, ReactHumanInTheLoop} from "@copilotkit/react-core/v2";

import {Store, useStore} from "@tanstack/react-store";
import {useEffect} from "react";

import {intentStore} from "./intent-store";

export interface NamespacePayload {
  context?: Record<string, unknown>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  tools?: ReactFrontendTool<any>[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  hitlTools?: ReactHumanInTheLoop<any>[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onIntent?: (action: string, data: any) => void;
}

export interface ConversationBlocker {
  id: string;
  kind: "chat-approval" | "modal-hitl";
  label: string;
}

export interface AIState {
  namespaces: Record<string, NamespacePayload>;
  conversationBlockers: Record<string, ConversationBlocker>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  renderers: Record<string, any>;
  /** Map of toolCall.id -> string context (e.g. userId) */
  toolCallContexts: Record<string, string>;
  registerConversationBlocker: (blocker: ConversationBlocker) => void;
  unregisterConversationBlocker: (blockerId: string) => void;
  registerPage: (namespace: string, payload: NamespacePayload) => void;
  unregisterPage: (namespace: string) => void;
  setToolCallContext: (toolCallId: string, context: string) => void;
}

/**
 * TanStack Store instance for AI context and capabilities.
 * Created at module level — stable instance across renders.
 */
export const aiStore = new Store<AIState>({
  namespaces: {},
  conversationBlockers: {},
  renderers: {},
  toolCallContexts: {},

  registerConversationBlocker: (blocker: ConversationBlocker) => {
    aiStore.setState((state) => ({
      ...state,
      conversationBlockers: {
        ...state.conversationBlockers,
        [blocker.id]: blocker,
      },
    }));
  },

  unregisterConversationBlocker: (blockerId: string) => {
    aiStore.setState((state) => {
      if (!state.conversationBlockers[blockerId]) return state;

      const nextBlockers = {...state.conversationBlockers};

      delete nextBlockers[blockerId];

      return {
        ...state,
        conversationBlockers: nextBlockers,
      };
    });
  },

  setToolCallContext: (toolCallId: string, context: string) => {
    aiStore.setState((state) => ({
      ...state,
      toolCallContexts: {
        ...state.toolCallContexts,
        [toolCallId]: context,
      },
    }));
  },

  registerPage: (namespace: string, payload: NamespacePayload) => {
    aiStore.setState((state) => {
      const newRenderers = {...state.renderers};

      payload.tools?.forEach((t) => {
        if (t.render) newRenderers[t.name] = t.render;
      });

      payload.hitlTools?.forEach((t) => {
        if (t.render) newRenderers[t.name] = t.render;
      });

      return {
        ...state,
        namespaces: {
          ...state.namespaces,
          [namespace]: payload,
        },
        renderers: newRenderers,
      };
    });
  },

  unregisterPage: (namespace: string) => {
    aiStore.setState((state) => {
      const newNamespaces = {...state.namespaces};

      delete newNamespaces[namespace];

      return {
        ...state,
        namespaces: newNamespaces,
      };
    });
  },
});

/**
 * React hook to access AI state.
 */
export function useAIStore<T>(selector: (state: AIState) => T): T {
  return useStore(aiStore, selector);
}

/**
 * Hook for pages to register their semantic context and tools.
 *
 * @param namespace Unique identifier for the page/component
 * @param payload The context and tools to register
 * @param dependencies Array of dependencies that should trigger a re-registration
 */
export function usePageAIContext(
  namespace: string,
  payload: NamespacePayload,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  dependencies: any[] = [],
) {
  useEffect(() => {
    aiStore.state.registerPage(namespace, payload);

    return () => {
      aiStore.state.unregisterPage(namespace);
    };
  }, [namespace, ...dependencies]);

  useEffect(() => {
    // 1. Subscribe to future intent changes
    const unsubscribe = intentStore.subscribe(() => {
      const currentIntent = intentStore.state.intent;

      if (currentIntent?.namespace === namespace) {
        payload.onIntent?.(currentIntent.action, currentIntent.data);
        intentStore.setState(() => ({intent: null}));
      }
    });

    // 2. Check immediately on mount in case intent was dispatched before we mounted
    const initialIntent = intentStore.state.intent;

    if (initialIntent?.namespace === namespace) {
      payload.onIntent?.(initialIntent.action, initialIntent.data);
      intentStore.setState(() => ({intent: null}));
    }

    return () => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const unsub = unsubscribe as any;

      if (typeof unsub === "function") {
        unsub();
      } else if (unsub && typeof unsub.unsubscribe === "function") {
        unsub.unsubscribe();
      }
    };
  }, [namespace, payload.onIntent]);
}
